import React, { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

interface PlayerStatsProps {
  character: any;
}

export const PlayerStats: React.FC<PlayerStatsProps> = ({ character }) => {
  const [stats] = useState({
    level: 1,
    experience: 0,
    nextLevelExp: 1000,
    health: 100,
    maxHealth: 100,
    mana: 50,
    maxMana: 50,
    gold: 250,
    strength: 10,
    intelligence: 12,
    dexterity: 11,
    endurance: 10,
    charisma: 9,
  });

  const expProgress = (stats.experience / stats.nextLevelExp) * 100;
  const healthProgress = (stats.health / stats.maxHealth) * 100;
  const manaProgress = (stats.mana / stats.maxMana) * 100;

  return (
    <Tabs defaultValue="stats" className="w-full">
      <TabsList className="grid w-full grid-cols-3 bg-slate-700">
        <TabsTrigger value="stats" className="text-xs">
          Stats
        </TabsTrigger>
        <TabsTrigger value="inventory" className="text-xs">
          Inventory
        </TabsTrigger>
        <TabsTrigger value="skills" className="text-xs">
          Skills
        </TabsTrigger>
      </TabsList>

      <TabsContent value="stats" className="space-y-4 mt-4">
        {/* Level & Experience */}
        <div className="space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-gray-300">Level</span>
            <span className="text-orange-400 font-bold">{stats.level}</span>
          </div>
          <div className="flex justify-between text-xs text-gray-400 mb-1">
            <span>Experience</span>
            <span>
              {stats.experience} / {stats.nextLevelExp}
            </span>
          </div>
          <Progress value={expProgress} className="h-2" />
        </div>

        {/* Health & Mana Bars */}
        <div className="space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-red-400">Health</span>
            <span className="text-red-400 font-bold">
              {stats.health} / {stats.maxHealth}
            </span>
          </div>
          <Progress value={healthProgress} className="h-3 bg-red-900" />
        </div>

        <div className="space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-blue-400">Mana</span>
            <span className="text-blue-400 font-bold">
              {stats.mana} / {stats.maxMana}
            </span>
          </div>
          <Progress value={manaProgress} className="h-3 bg-blue-900" />
        </div>

        {/* Gold */}
        <div className="flex justify-between items-center bg-slate-700 p-2 rounded">
          <span className="text-gray-300 text-sm">Gold</span>
          <span className="text-yellow-400 font-bold">{stats.gold}</span>
        </div>

        {/* Attributes */}
        <div className="bg-slate-700 p-3 rounded space-y-2">
          <p className="text-orange-400 font-semibold text-sm mb-3">Attributes</p>
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div className="flex justify-between">
              <span className="text-gray-300">Strength</span>
              <span className="text-white font-bold">{stats.strength}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-300">Intelligence</span>
              <span className="text-white font-bold">{stats.intelligence}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-300">Dexterity</span>
              <span className="text-white font-bold">{stats.dexterity}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-300">Endurance</span>
              <span className="text-white font-bold">{stats.endurance}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-300">Charisma</span>
              <span className="text-white font-bold">{stats.charisma}</span>
            </div>
          </div>
        </div>
      </TabsContent>

      <TabsContent value="inventory" className="space-y-2 mt-4">
        <div className="bg-slate-700 p-3 rounded space-y-2 max-h-64 overflow-y-auto">
          <p className="text-orange-400 font-semibold text-sm">Equipment</p>
          <div className="space-y-1 text-sm">
            <div className="flex justify-between text-gray-300">
              <span>Weapon:</span>
              <span className="text-white font-semibold">{character.weapon}</span>
            </div>
            <div className="flex justify-between text-gray-300">
              <span>Outfit:</span>
              <span className="text-white font-semibold capitalize">{character.outfit}</span>
            </div>
            {character.accessories.length > 0 && (
              <div>
                <p className="text-gray-300 text-xs mb-1">Accessories:</p>
                {character.accessories.map((acc: string) => (
                  <div key={acc} className="text-gray-400 text-xs ml-2">
                    • {acc}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="border-t border-gray-600 pt-3 mt-3">
            <p className="text-orange-400 font-semibold text-sm mb-2">Items</p>
            <div className="space-y-1 text-xs text-gray-400">
              <p>• Health Potion x2</p>
              <p>• Mana Potion x1</p>
              <p>• Ancient Coin x5</p>
              <p>• Mystery Amulet x1</p>
            </div>
          </div>
        </div>
      </TabsContent>

      <TabsContent value="skills" className="space-y-2 mt-4">
        <div className="bg-slate-700 p-3 rounded space-y-3 max-h-64 overflow-y-auto">
          <p className="text-orange-400 font-semibold text-sm">Active Skills</p>
          <div className="space-y-2 text-xs">
            <div className="border border-orange-600 rounded p-2">
              <p className="text-orange-400 font-semibold">Slash</p>
              <p className="text-gray-400">Physical attack dealing extra damage</p>
              <p className="text-gray-500 mt-1">Cooldown: Ready</p>
            </div>
            <div className="border border-gray-600 rounded p-2">
              <p className="text-white font-semibold">Defensive Stance</p>
              <p className="text-gray-400">Reduce incoming damage by 30%</p>
              <p className="text-gray-500 mt-1">Cooldown: 5s</p>
            </div>
          </div>
        </div>
      </TabsContent>
    </Tabs>
  );
};
